import { useEffect, useRef } from 'react';
import { getPaymentInfo } from '@/redux/actions/payment.action';

interface UsePaymentPollingParams {
  paymentId: string | null;
  successProcessed: boolean;
  errorProcessed: boolean;
  handledRef: React.MutableRefObject<boolean>;
  goToSuccess: () => void;
  errorPayment: (urlParam: string) => void;
}

export function usePaymentPolling({
  paymentId,
  successProcessed,
  errorProcessed,
  handledRef,
  goToSuccess,
  errorPayment,
}: UsePaymentPollingParams): void {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptCountRef = useRef<number>(0);
  const MAX_ATTEMPTS = 330;
  const POLLING_INTERVAL = 2000;

  useEffect(() => {
    if (!paymentId || successProcessed || errorProcessed || handledRef.current) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const checkPaymentStatus = async () => {
      if (attemptCountRef.current >= MAX_ATTEMPTS) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        errorPayment('timeout');
        return;
      }

      attemptCountRef.current += 1;

      try {
        const paymentInfo = await getPaymentInfo(paymentId);

        if (!paymentInfo) {
          return;
        }

        const status = paymentInfo.status;

        if (status === 'succeeded') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          goToSuccess();
        } else if (status === 'canceled') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          errorPayment('');
        }
      } catch (error) {
        console.error('Ошибка проверки статуса платежа:', error);
      }
    };

    intervalRef.current = setInterval(checkPaymentStatus, POLLING_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [paymentId, successProcessed, errorProcessed, handledRef, goToSuccess, errorPayment]);
}
